import React, { useState, useEffect } from 'react';
import AdsManagerBody from './AdsManagerBody';
import AdsManagerHeader from './AdsManagerHeader';

import { channels } from './constants';
import { ProviderNames } from 'lib/data/models/Providers';
import { PageHelper } from 'lib/helpers/PageHelper';
import { RequestStatus } from 'lib/RequestManager';
import { combineAllDailyCampaignData } from 'utils/adaya/ProviderUtils';
import { getLogger } from 'utils/logging';
import { getInnerError, renderLoader, getAccountTimezone, getAccountProviders } from 'app/lib/utils/adaya/AdayaUtils';
import { getStoreDate } from 'utils/DateUtils';
import { sortCampaigns } from './utils';

import './style.scss';

const { log, warn } = getLogger('AdsManager', { color: 'yellow', enabled: false });
const ADS_MANAGER_DEPS = () => ['user', 'campaign-data-merged-today', 'campaign-data-merged-yesterday', 'campaign-data-merged-last-7'];

const pageHelper = new PageHelper();

const AdsManagerUpdate = (props) => {
    const user = props.auth.user;
    const account = user?.activeAccount;
    const timezone = getAccountTimezone(account);
    const [activeTab, setActiveTab] = useState('campaigns');
    const [metricOption, setMetricOption] = useState('ROAS');
    const [performanceOption, setPerformanceOption] = useState('Highest');
    const [dateRange, setDateRange] = useState([moment(), moment()]);
    const [selChannels, setSelChannels] = useState(channels);
    const [resultData, setResultData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState(undefined);

    useEffect(() => {
        fetchData();
    }, [dateRange, selChannels]);

    const fetchData = (fetchNewData = false) => {
        if (!account) return;
        if (selChannels.length === 0) {
            setResultData([]);
            return;
        }
        setLoading(true);
        pageHelper
            .getDailyCampaignDataRequest(getFilterFromSelection(), {
                dontFetchNewData: !fetchNewData, // QAM won't ask server for data by defaultErrorMessage
                resolveUnknownObjects: true, // let QAM resolve the unknown ad object IDs->names
                clearDataBefore: false,
                forceUpdate: false
            })
            .then((res) => {
                onDataResponse(res);
                setLoading(false);
            })
            .catch((err) => {
                console.log('error log:', err);
                setError(getInnerError(err));
                setLoading(false);
            });
    };

    useEffect(() => {
        setFilteredData(sortCampaigns(resultData, performanceOption, metricOption, user));
    }, [resultData, performanceOption, metricOption]);

    const onDataResponse = (r) => {
        try {
            if (!r) throw 'Response was empty.';
            log('onDataResponse', r);

            if (r.status == RequestStatus.FINISHED) {
                // update UI data if the response matches the current request filter
                if (pageHelper.requestMatchesKey(r, getFilterFromSelection())) {
                    log(`Raw response:`, r.response, typeof r.response == 'object' ? `${Object.keys(r.response).length} days` : '(no data)');

                    let campaignData = combineAllDailyCampaignData(r.response);

                    // not ideal but the callback/event system seems to not understand/detect the state changes.
                    // if anyone has a solution please let me know, haven't had proper time. (RW)
                    // setData(undefined);
                    // setTimeout(() => {
                    setResultData(campaignData.campaigns); // maybe it works?
                    //}, 0);
                } else {
                    log('Current request response does not match current select, not showing data. Request key:', r.key);
                }
            } else {
                // shouldn't get here, it's handled through exceptions mostly, but just in case.
                setError(getInnerError(r));
            }
        } catch (e) {
            log(`Exception`, e);
            throw e;
        }
    };

    const getFilterFromSelection = () => {
        const timeRange = {
            start_date: getStoreDate(dateRange[0], timezone).startOf('day').toISOString(),
            end_date: getStoreDate(dateRange[1], timezone).endOf('day').toISOString()
        };
        if (!account) throw `No account found for the current user.`;
        const providers = selChannels.length === channels.length ?
            getAccountProviders(account, [ProviderNames.SHOPIFY]).map((p) => p.name) :
            getAccountProviders(account, [ProviderNames.SHOPIFY], selChannels.map(item => item.value)).map((p) => p.name);
        return { account, timeRange, providers };
    };

    const onRefresh = () => {
        fetchData(true);
    };

    return (
        <div className="ads-manager-wrapper">
            <div className="ads-manager-container" style={{ height: isLoading ? '100%' : 'auto' }}>
                <AdsManagerHeader
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    onRefresh={onRefresh}
                    isLoading={isLoading}
                    metricOption={metricOption}
                    setMetricOption={setMetricOption}
                    performanceOption={performanceOption}
                    setPerformanceOption={setPerformanceOption}
                    selChannels={selChannels}
                    setSelChannels={setSelChannels}
                />
                {isLoading ? renderLoader() : <AdsManagerBody activeTab={activeTab} resultData={filteredData} />}
            </div>
        </div>
    );
};

// export default makeWaitForData(AdsManagerUpdate, ADS_MANAGER_DEPS, 'ads-manager');
export default AdsManagerUpdate;
